import { useState, useEffect } from "preact/hooks"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { DeductionDetailJson, DeductionSearchResult, searchDeductions, updateDeduction } from "@/api/deduction"
import { exactCurrencyFormatter } from "@/utils"
import { format, parseISO } from "date-fns"
import { Check, Loader2 } from "lucide-react"
import { toast } from "@/components/ui/use-toast"
import { RefreshFlag } from "@/refresh_flag"

interface AssignRepaymentDialogProps {
  deduction: DeductionDetailJson
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function AssignRepaymentDialog({ deduction, open, onOpenChange }: AssignRepaymentDialogProps) {
  const [results, setResults] = useState<DeductionSearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [revertOldStatus, setRevertOldStatus] = useState(true)

  useEffect(() => {
    if (open) {
      setSearchQuery(deduction.invoice_stem)
      loadResults(deduction.invoice_stem)
    }
  }, [open, deduction.invoice_stem])

  async function loadResults(query: string) {
    setLoading(true)
    try {
      const targets = await searchDeductions({
        q: query || undefined,
        source: deduction.source,
        exclude_repayments: true,
        kinds: "deduction,invoice",
      })
      targets.sort((a: DeductionSearchResult, b: DeductionSearchResult) => amountDistance(a) - amountDistance(b))
      setResults(targets)
    } catch (e) {
      console.error("Failed to load repayment targets", e)
    } finally {
      setLoading(false)
    }
  }

  function handleSearchChange(value: string) {
    setSearchQuery(value)
    loadResults(value)
  }

  async function handleAssign() {
    if (!selectedId) return
    setSaving(true)
    try {
      await updateDeduction(deduction.id, {
        repayment_for: selectedId,
        revert_old_status: deduction.repayment_for ? revertOldStatus : undefined,
      })
      toast({ title: "Success", description: "Deduction assigned successfully" })
      RefreshFlag.set(flag => !flag)
      onOpenChange(false)
    } catch (e) {
      toast({ title: "Error", description: "Failed to assign deduction" })
    } finally {
      setSaving(false)
    }
  }

  function handleClose() {
    setSelectedId(null)
    setSearchQuery("")
    setRevertOldStatus(true)
    onOpenChange(false)
  }

  const repayment_amount = Math.abs(deduction.invoice_amount ?? 0)

  function getCandidateWinAmount(item: DeductionSearchResult): number {
    return item.kind === "invoice"
      ? Math.abs(item.discount_amount ?? 0)
      : Math.abs(item.invoice_amount)
  }

  function amountDistance(item: DeductionSearchResult): number {
    const deductionDist = Math.abs(getCandidateWinAmount(item) - repayment_amount)
    if (item.dispute_amount != null) {
      const disputeDist = Math.abs(Math.abs(item.dispute_amount) - repayment_amount)
      return Math.min(deductionDist, disputeDist)
    }
    return deductionDist
  }

  function isRecommendedCandidate(item: DeductionSearchResult): boolean {
    if (!item.dispute_id || item.dispute_amount == null) return false
    const disputeAmount = Math.abs(item.dispute_amount)
    return Math.abs(disputeAmount - repayment_amount) < 0.01
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{deduction.repayment_for ? "Change Original Deduction" : "Assign to Deduction"}</DialogTitle>
          <p className="text-sm text-gray-600 mt-1">Search for the original deduction that this repayment is for.</p>
          <p className="text-sm text-gray-700 mt-1">
            Repayment / Win Amount: <span className="font-semibold">{exactCurrencyFormatter(repayment_amount)}</span>
          </p>
        </DialogHeader>
        <div className="py-2">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search by invoice # or PO #..."
              value={searchQuery}
              onValueChange={handleSearchChange}
            />
            <CommandList className="h-[300px]">
              {loading ? (
                <div className="py-6 text-center text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                  Loading...
                </div>
              ) : (
                <>
                  <CommandEmpty>No deductions found. Try deleting a few characters from your search. Otherwise, the original deduction may not exist in the system or may not match your search.</CommandEmpty>
                  <CommandGroup>
                    {results.map(item => (
                      <CommandItem
                        key={item.id}
                        value={item.id}
                        onSelect={() => setSelectedId(item.id)}
                        className={`cursor-pointer ${
                          isRecommendedCandidate(item) ? "bg-green-500/5 hover:bg-green-500/10" : ""
                        }`}>
                        <Check className={`mr-2 h-4 w-4 ${selectedId === item.id ? "opacity-100" : "opacity-0"}`} />
                        <div className="flex flex-col flex-1">
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{item.invoice_number}</span>
                              {isRecommendedCandidate(item) && (
                                <span className="inline-flex items-center rounded-full bg-green-500/15 text-green-700 px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap">
                                  Recommended
                                </span>
                              )}
                              <button
                                type="button"
                                className="text-xs text-indigo-600 hover:text-indigo-700 underline whitespace-nowrap"
                                onClick={event => {
                                  event.preventDefault()
                                  event.stopPropagation()
                                  window.open(`/deduction/${item.id}`, "_blank")
                                }}>
                                View Deduction
                              </button>
                            </div>
                            <span className="text-sm text-gray-600">
                              {exactCurrencyFormatter(getCandidateWinAmount(item))}
                            </span>
                          </div>
                          <div className="flex justify-between text-xs text-gray-500">
                            <span>
                              {format(parseISO(item.invoice_date), "MMM d, yyyy")}
                              {item.po_number && ` | PO: ${item.po_number}`}
                              {item.kind === "invoice" &&
                                item.gross_amount &&
                                ` | Gross: ${exactCurrencyFormatter(item.gross_amount)}`}
                            </span>
                            <span>{item.kind === "invoice" ? "Early Pay" : item.category || item.kind}</span>
                          </div>
                          <div className="mt-1 text-xs text-gray-500">
                            {item.dispute_id
                              ? `Disputed${
                                  item.dispute_amount != null ? ` (${exactCurrencyFormatter(Math.abs(item.dispute_amount))})` : ""
                                }`
                              : "Not disputed"}
                          </div>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </div>
        {deduction.repayment_for && (
          <label className="flex items-center gap-2 text-sm text-gray-700 px-1">
            <input
              type="checkbox"
              checked={revertOldStatus}
              onChange={e => setRevertOldStatus((e.target as HTMLInputElement).checked)}
              className="rounded border-gray-300"
            />
            Change current deduction's status from "won" to "review"
          </label>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleAssign} disabled={!selectedId || saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
